import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccessControlService, CapabilityDTO } from 'src/app/services/access-control.service';
import { ToastService } from 'src/app/services/toast.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import Swal from 'sweetalert2';

interface CategoryGroup {
  name: string;
  caps: CapabilityDTO[];
}

/**
 * EAS-198 — atribuição de permissões de ação por usuário/marina.
 * Perfil Admin/Visualização (presets) ou Personalizado (capacidades por área).
 */
@Component({
  selector: 'app-user-access',
  templateUrl: './user-access.component.html',
  styleUrls: ['./user-access.component.scss']
})
export class UserAccessComponent implements OnInit {

  userId: number;
  marinaId: number;
  userName = '';

  profile: 'ADMIN' | 'VIEWER' | 'CUSTOM' = 'VIEWER';
  categories: CategoryGroup[] = [];
  selected: { [code: string]: boolean } = {};

  readonly profileOptions: Array<{ value: 'ADMIN' | 'VIEWER' | 'CUSTOM'; icon: string; title: string; subtitle: string }> = [
    { value: 'ADMIN', icon: 'pi-shield', title: 'Administrador', subtitle: 'Acesso total ao sistema' },
    { value: 'VIEWER', icon: 'pi-eye', title: 'Visualização', subtitle: 'Somente consulta' },
    { value: 'CUSTOM', icon: 'pi-sliders-h', title: 'Personalizado', subtitle: 'Permissões por ação' }
  ];

  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private spinner: NgxSpinnerService,
    private accessService: AccessControlService,
    private toasty: ToastService
  ) {}

  ngOnInit(): void {
    const user = this.config.data ? this.config.data.user : null;
    this.userId = user ? user.id : null;
    this.userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    this.marinaId = StorageUtil.getMarinaId();
    this.load();
  }

  private load(): void {
    this.spinner.show();
    this.accessService.getCatalog().subscribe({
      next: (catalog) => {
        this.categories = this.groupByCategory(catalog);
        catalog.forEach((c) => (this.selected[c.code] = false));
        this.accessService.getUserAccess(this.userId, this.marinaId).subscribe({
          next: (access) => {
            this.profile = access.profile || 'VIEWER';
            (access.capabilityCodes || []).forEach((code) => (this.selected[code] = true));
            this.spinner.hide();
          },
          error: () => this.spinner.hide()
        });
      },
      error: () => {
        this.spinner.hide();
        Swal.fire('Atenção', 'Não foi possível carregar as permissões.', 'warning');
      }
    });
  }

  private groupByCategory(catalog: CapabilityDTO[]): CategoryGroup[] {
    const map = new Map<string, CapabilityDTO[]>();
    catalog.forEach((c) => {
      if (!map.has(c.category)) {
        map.set(c.category, []);
      }
      map.get(c.category).push(c);
    });
    return Array.from(map.entries()).map(([name, caps]) => ({ name, caps }));
  }

  get isCustom(): boolean {
    return this.profile === 'CUSTOM';
  }

  selectProfile(value: 'ADMIN' | 'VIEWER' | 'CUSTOM'): void {
    this.profile = value;
  }

  categoryIcon(name: string): string {
    const n = (name || '').toLowerCase();
    if (n.includes('fatur')) { return 'pi-dollar'; }
    if (n.includes('cadastr')) { return 'pi-id-card'; }
    if (n.includes('usu')) { return 'pi-users'; }
    if (n.includes('config')) { return 'pi-cog'; }
    return 'pi-folder';
  }

  /**
   * Rótulo de exibição da categoria. O catálogo (backend) envia os nomes em CAIXA ALTA e
   * sem acento (ex.: USUARIOS, CONFIGURACOES); aqui normalizamos para exibição acentuada.
   */
  categoryLabel(name: string): string {
    const normalized = (name || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase();
    const labels: { [key: string]: string } = {
      FATURAMENTO: 'Faturamento',
      CADASTRO: 'Cadastro',
      USUARIOS: 'Usuários',
      CONFIGURACOES: 'Configurações'
    };
    if (labels[normalized]) {
      return labels[normalized];
    }
    // fallback: primeira letra maiúscula, resto minúsculo
    return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : name;
  }

  selectedCount(group: CategoryGroup): number {
    return group.caps.filter((c) => this.selected[c.code]).length;
  }

  toggleCategory(group: CategoryGroup, value: boolean): void {
    group.caps.forEach((c) => (this.selected[c.code] = value));
  }

  save(): void {
    const capabilityCodes = this.profile === 'CUSTOM'
      ? Object.keys(this.selected).filter((code) => this.selected[code])
      : [];
    this.spinner.show();
    this.accessService.setUserAccess(this.userId, this.marinaId, { profile: this.profile, capabilityCodes })
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.toasty.success('Permissões salvas com sucesso!');
          this.ref.close(true);
        },
        error: (error) => {
          this.spinner.hide();
          const msg = error && error.error && error.error.message ? error.error.message : 'Falha ao salvar permissões.';
          Swal.fire('Atenção', msg, 'warning');
        }
      });
  }
}
